import type { TaskStatus } from '@/lib/api/types';

const DB_NAME = 'tendedloop-operator';
const DB_VERSION = 1;
const STORE_NAME = 'pending-updates';

export interface QueuedTaskUpdate {
  id: string;
  taskId: string;
  status: TaskStatus;
  timestamp: number;
  retryCount: number;
}

class OfflineTaskQueue {
  private db: IDBDatabase | null = null;
  private isOnline = navigator.onLine;
  private syncInProgress = false;
  private onSyncComplete: (() => void) | null = null;

  constructor() {
    this.init();
    this.setupNetworkListeners();
  }

  private async init() {
    return new Promise<void>((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        this.db = request.result;
        resolve();
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          const store = db.createObjectStore(STORE_NAME, { keyPath: 'id' });
          store.createIndex('taskId', 'taskId', { unique: false });
          store.createIndex('timestamp', 'timestamp', { unique: false });
        }
      };
    });
  }

  private setupNetworkListeners() {
    window.addEventListener('online', () => {
      this.isOnline = true;
      this.sync();
    });

    window.addEventListener('offline', () => {
      this.isOnline = false;
    });
  }

  async add(taskId: string, status: TaskStatus): Promise<string> {
    await this.ensureDb();

    const item: QueuedTaskUpdate = {
      id: `update-${Date.now()}-${Math.random().toString(36).slice(2)}`,
      taskId,
      status,
      timestamp: Date.now(),
      retryCount: 0,
    };

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.add(item);

      request.onsuccess = () => resolve(item.id);
      request.onerror = () => reject(request.error);
    });
  }

  async remove(id: string): Promise<void> {
    await this.ensureDb();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.delete(id);

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  async getAll(): Promise<QueuedTaskUpdate[]> {
    await this.ensureDb();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([STORE_NAME], 'readonly');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.getAll();

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  async getCount(): Promise<number> {
    await this.ensureDb();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([STORE_NAME], 'readonly');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.count();

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  async getPendingForTask(taskId: string): Promise<QueuedTaskUpdate | null> {
    await this.ensureDb();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([STORE_NAME], 'readonly');
      const store = transaction.objectStore(STORE_NAME);
      const index = store.index('taskId');
      const request = index.getAll(taskId);

      request.onsuccess = () => {
        // Return the most recent pending update for this task
        const updates = request.result.sort((a, b) => b.timestamp - a.timestamp);
        resolve(updates[0] || null);
      };
      request.onerror = () => reject(request.error);
    });
  }

  async updateRetryCount(id: string, retryCount: number): Promise<void> {
    await this.ensureDb();

    const items = await this.getAll();
    const item = items.find(i => i.id === id);
    if (!item) return;

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.put({ ...item, retryCount });

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  async sync(): Promise<{ success: number; failed: number }> {
    if (this.syncInProgress || !this.isOnline) {
      return { success: 0, failed: 0 };
    }

    this.syncInProgress = true;
    let success = 0;
    let failed = 0;

    try {
      const items = await this.getAll();
      const API_BASE = import.meta.env.VITE_API_URL || '/api';
      const token = localStorage.getItem('accessToken');

      if (!token) {
        this.syncInProgress = false;
        return { success: 0, failed: items.length };
      }

      for (const item of items) {
        if (item.retryCount >= 3) {
          // Too many retries, remove from queue
          await this.remove(item.id);
          failed++;
          continue;
        }

        try {
          const response = await fetch(`${API_BASE}/tasks/${item.taskId}/status`, {
            method: 'PATCH',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify({ status: item.status }),
          });

          if (response.ok) {
            await this.remove(item.id);
            success++;
          } else if (response.status === 401) {
            // Auth expired, stop syncing
            break;
          } else {
            await this.updateRetryCount(item.id, item.retryCount + 1);
            failed++;
          }
        } catch {
          await this.updateRetryCount(item.id, item.retryCount + 1);
          failed++;
        }
      }

      if (this.onSyncComplete) {
        this.onSyncComplete();
      }
    } finally {
      this.syncInProgress = false;
    }

    return { success, failed };
  }

  onSync(callback: () => void): () => void {
    this.onSyncComplete = callback;
    // Return unsubscribe function
    return () => {
      if (this.onSyncComplete === callback) {
        this.onSyncComplete = null;
      }
    };
  }

  get online() {
    return this.isOnline;
  }

  private async ensureDb() {
    if (!this.db) {
      await this.init();
    }
  }
}

export const offlineTaskQueue = new OfflineTaskQueue();
